import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AngularFireMessaging } from '@angular/fire/compat/messaging';

import { environment } from 'src/environments/environment';
import { LocalStorageService } from './local-storage-service';
import { NotificationItem, NotificationStateService } from './notification-state.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root'
})
export class FcmService {
  private baseUrl: string = environment.baseUrl;

  constructor(
    private afMessaging: AngularFireMessaging,
    private localStorageService: LocalStorageService,
    private http: HttpClient,
    private UserService: UserService,
    private notificationStateService: NotificationStateService
  ) { }

  public async installFCMServiceWorker() {
    try {
      if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();

        const hasFCMWorker = registrations.some(reg =>
          reg.active?.scriptURL.includes('firebase-messaging')
        );

        if (!hasFCMWorker) {
          console.log('Installing FCM service worker...');
          await this.registerSw();
        } else {
          console.log('FCM service worker already registered.');
        }

        this.requestNotificationPermission();
      }
    } catch (error) {
      console.error('Error installing FCM service worker:', error);
    }
  }

  private async registerSw(): Promise<ServiceWorkerRegistration | null> {
    try {
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
        scope: '/',
        type: 'classic'
      });
      console.log('Service Worker registered with scope:', registration.scope);
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return null;
    }
  }

  public async requestNotificationPermission() {
    const permission = Notification.permission;

    if (permission === 'granted') {
      console.log('Notification permission already granted.');
      this.getFcmToken();
    } else if (permission === 'default') {
      console.log('Requesting notification permission...');
      const result = await Notification.requestPermission();

      if (result === 'granted') {
        console.log('Notification permission granted.');
        this.getFcmToken();
      } else {
        console.warn('Notification permission denied.');
      }
    } else {
      console.warn('Notification permission previously denied.');
    }
  }

  public getFcmToken() {
    console.log('Retrieving FCM token...');

    this.afMessaging.getToken.subscribe({
      next: (token) => {
        if (token) {
          console.log('FCM Token:', token);
          this.saveNotificationToken(token);
        } else {
          console.warn('No FCM token available.');
        }
      },
      error: (error) => {
        console.error('Error retrieving FCM token:', error);
      }
    });
  }

  public deleteToken(token: string) {
    this.afMessaging.deleteToken(token).subscribe({
      next: (success) => {
        console.log('Token deleted successfully:', success);
      },
      error: (error) => {
        console.error('Error deleting token:', error);
      }
    });
  }

  public listenForMessages() {
    this.afMessaging.messages.subscribe({
      next: (message: any) => {
        console.log('Message received:', message);

        const notification: NotificationItem = {
          is_active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          module: 'Notificación',
          body: {
            body: message.notification?.body || '',
            title: message.notification?.title || '',
            description: message.notification?.body || ''
          },
          description: message.notification?.body || '',
          url: '',
          read: false,
          made_at: {
            id: this.UserService.Employee.id,
            name: this.UserService.Employee.name,
            job_title: this.UserService.Employee.job_title || '',
            color: this.UserService.Employee.color || '#007bff',
            signature: this.UserService.Employee.signature || ''
          },
          job_center: this.UserService.Employee.job_center?.id || ''
        };

        this.notificationStateService.addNotification(notification);
      },
      error: (error) => {
        console.error('Error receiving messages:', error);
      }
    });
  }

  private saveNotificationToken(token: string) {
    this.localStorageService.set('fcm_token', token);
    this.sendTokenToBackend(token);
  }

  private async sendTokenToBackend(token: string) {
    try {
      const data = {
        token: token,
        portal: "WEB",
        user: this.UserService.Employee.id
      };

      await this.http.post(`${this.baseUrl}/device-tokens/`, data).toPromise();
      console.log('Token sent to backend successfully');
    } catch (error) {
      console.error('Error sending token to backend:', error);
    }
  }
}
